import { getTranslations } from "@/lib/translations";
import { z } from "zod";

import { requireAdmin } from "@/core/auth/admin-guard";
import { inviteStaff, createTeamMemberWithInvite, syncTeamMemberAuthRole } from "@/core/repositories/auth.repository";
import { createTeamService } from "@/core/services/factory";
import { INVITE_AUTH_CALLBACK_PATH } from "@/lib/auth-routes";
import type {
  CreateTeamMemberState,
  ResendTeamMemberInviteResult,
  UpdateTeamMemberState,
} from "@/core/services/types/team.types";

const teamMemberSchema = z.object({
  first_name: z.string().trim().min(1, "firstNameRequired"),
  last_name: z.string().trim().min(1, "lastNameRequired"),
  email: z.email("invalidEmail"),
  role: z.enum(["ADMIN", "EDITOR"]),
  is_active: z.preprocess(
    (value) => value === "true" || value === true || value === "on",
    z.boolean(),
  ),
  avatar_url: z.preprocess(
    (value) =>
      typeof value === "string" && value.trim() === "" ? null : value,
    z.url("invalidAvatarUrl").nullable(),
  ),
});

function extractFieldErrors(error: z.ZodError): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  for (const issue of error.issues) {
    const field = issue.path[0];
    if (typeof field === "string" && !fieldErrors[field]) {
      fieldErrors[field] = issue.message;
    }
  }

  return fieldErrors;
}

function mapValidationMessages(
  fieldErrors: Record<string, string>,
  t: ReturnType<typeof getTranslations>,
): Record<string, string> {
  const mapped: Record<string, string> = {};

  for (const [field, messageKey] of Object.entries(fieldErrors)) {
    mapped[field] = t(messageKey);
  }

  return mapped;
}

function mapInviteError(
  error: string | undefined,
  t: ReturnType<typeof getTranslations>,
): string {
  const message = (error ?? "").toLowerCase();

  if (message.includes("rate limit")) {
    return t("inviteRateLimited");
  }

  if (
    message.includes("already been registered") ||
    message.includes("already exists") ||
    message.includes("user already registered")
  ) {
    return t("inviteUserExists");
  }

  if (message.includes("redirect") || message.includes("invalid url")) {
    return t("inviteRedirectNotAllowed");
  }

  if (message === "not_in_team") {
    return t("inviteNotInTeam");
  }

  if (message === "inactive") {
    return t("inviteInactive");
  }

  if (
    message === "team_lookup_failed" ||
    message === "missing_service_role"
  ) {
    return t("inviteServiceMisconfigured");
  }

  if (message === "create_failed") {
    return t("createFailed");
  }

  return t("inviteFailed");
}

export async function createTeamMember(
  _prevState: CreateTeamMemberState,
  formData: FormData,
): Promise<CreateTeamMemberState> {
  await requireAdmin();

  const t = getTranslations("team.validation");

  const parsed = teamMemberSchema.safeParse({
    first_name: formData.get("first_name"),
    last_name: formData.get("last_name"),
    email: formData.get("email"),
    role: formData.get("role"),
    is_active: formData.get("is_active"),
    avatar_url: formData.get("avatar_url"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: mapValidationMessages(extractFieldErrors(parsed.error), t),
    };
  }

  const redirectTo = INVITE_AUTH_CALLBACK_PATH;
  const createResult = await createTeamMemberWithInvite(parsed.data, redirectTo);

  if (!createResult.success) {
    return {
      success: false,
      error: mapInviteError(createResult.error, t),
    };
  }

  return {
    success: true,
    data: createResult.data,
    emailSent: createResult.emailSent,
    inviteUrl: createResult.inviteUrl,
  };
}

export async function updateTeamMember(
  _prevState: UpdateTeamMemberState,
  formData: FormData,
): Promise<UpdateTeamMemberState> {
  await requireAdmin();

  const t = getTranslations("team.validation");

  const updateTeamMemberSchema = teamMemberSchema.extend({
    id: z.uuid(),
  });

  const parsed = updateTeamMemberSchema.safeParse({
    id: formData.get("id"),
    first_name: formData.get("first_name"),
    last_name: formData.get("last_name"),
    email: formData.get("email"),
    role: formData.get("role"),
    is_active: formData.get("is_active"),
    avatar_url: formData.get("avatar_url"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: mapValidationMessages(extractFieldErrors(parsed.error), t),
    };
  }

  const { id, ...input } = parsed.data;
  const team = createTeamService();
  const updated = await team.updateMember(id, input);

  if (!updated) {
    return {
      success: false,
      error: t("updateFailed"),
    };
  }

  const syncResult = await syncTeamMemberAuthRole(updated.email);

  if (!syncResult.success) {
    return {
      success: false,
      error: t("roleSyncFailed"),
    };
  }

  return { success: true, data: updated };
}

export async function resendTeamMemberInvite(
  email: string,
): Promise<ResendTeamMemberInviteResult> {
  await requireAdmin();

  const t = getTranslations("team.validation");
  const normalizedEmail = email.trim().toLowerCase();

  if (!normalizedEmail) {
    return { success: false, error: t("invalidEmail") };
  }

  const inviteResult = await inviteStaff(normalizedEmail, INVITE_AUTH_CALLBACK_PATH, {
    resend: true,
  });

  if (!inviteResult.success) {
    return {
      success: false,
      error: mapInviteError(inviteResult.error, t),
    };
  }

  return {
    success: true,
    emailSent: inviteResult.emailSent,
    inviteUrl: inviteResult.inviteUrl,
  };
}
